// contexts/LoadingContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import LoadingSpinner from "../components/LoadingSpinner";

interface LoadingContextType {
  isLoading: boolean;
  loadingMessage: string;
  loadingProgress: number;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  updateProgress: (progress: number) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within LoadingProvider");
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider = ({ children }: LoadingProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const [loadingProgress, setLoadingProgress] = useState(0);

  const showLoading = useCallback((message: string = "Loading...") => {
    setLoadingMessage(message);
    setLoadingProgress(0);
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
    setLoadingMessage("Loading...");
    setLoadingProgress(0);
  }, []);

  const updateProgress = useCallback((progress: number) => {
    setLoadingProgress(Math.min(Math.max(progress, 0), 100));
  }, []);

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingMessage,
        loadingProgress,
        showLoading,
        hideLoading,
        updateProgress,
      }}
    >
      {children}
      {isLoading && (
        <LoadingSpinner
          overlay
          message={loadingMessage}
          progress={loadingProgress}
        />
      )}
    </LoadingContext.Provider>
  );
};
